// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.

/**
 * Sprint 62.57.0 (Slice D), gate E12.5 pass 1 FE-3 and pass 2 FE2-2 — the
 * update form instance created for an original filing but not yet linked to an
 * update filing.
 *
 * *Prepare an update filing* creates a form instance before it posts the
 * update. When the post is refused or the dialog is abandoned, the instance is
 * kept and reused by the next attempt on the same original, so a second draft
 * form is never created (the forms API deletes no instance under any grant).
 *
 * **Whose it is (FE2-2).** An instance belongs to the person who started it, in
 * their organisation. Both stores (the module `Map` the open SPA keeps and the
 * tab's `sessionStorage`, which survives a reload) are keyed by
 * `org_id:user_id:original filing id`, and an entry whose recorded owner is not
 * the reader is ignored. A sign-out of a password session does not reload the
 * window, so `AuthContext` also calls `forgetUnlinkedUpdateInstances()` on an
 * explicit sign-out and whenever the signed-in person or organisation changes:
 * on a shared browser, the next person never inherits the last one's draft
 * form. A session that only expires (a 401) forgets nothing (pass 3, FE3-4):
 * the same person signing back in reuses what they kept, so no second draft
 * form is created; the owner keys keep it from anyone else.
 *
 * Storage can throw or be absent (private window, blocked site data): the
 * module map alone is then the record, and the failure is logged by label only
 * (never the error, which may carry the stored value).
 */

import { logWarning } from './optional-operation-log.js';

/** The signed-in person an instance belongs to. */
export interface UpdateInstanceOwner {
  orgId: string;
  userId: string;
}

export interface UnlinkedUpdateInstance {
  instanceId: string;
  templateId: string;
}

interface StoredInstance extends UnlinkedUpdateInstance {
  orgId: string;
  userId: string;
}

export const UNLINKED_UPDATE_INSTANCE_STORAGE_PREFIX = 'grc:unlinked-update-instance:';

const held = new Map<string, StoredInstance>();

/** An owner with both ids, or null: nothing is kept for an unidentified reader. */
function usableOwner(owner: UpdateInstanceOwner | null | undefined): UpdateInstanceOwner | null {
  return owner && owner.orgId && owner.userId ? owner : null;
}

function keyOf(owner: UpdateInstanceOwner, originalId: string): string {
  return `${owner.orgId}:${owner.userId}:${originalId}`;
}

/** The instance this person started for this original and has not linked, or null. */
export function readUnlinkedUpdateInstance(
  owner: UpdateInstanceOwner | null | undefined,
  originalId: string,
): UnlinkedUpdateInstance | null {
  const who = usableOwner(owner);
  if (!who || !originalId) return null;
  const key = keyOf(who, originalId);
  // The map's key carries the owner, so an entry found there is this person's.
  const inMemory = held.get(key);
  if (inMemory) return { instanceId: inMemory.instanceId, templateId: inMemory.templateId };
  try {
    const raw = window.sessionStorage.getItem(UNLINKED_UPDATE_INSTANCE_STORAGE_PREFIX + key);
    const parsed = raw ? (JSON.parse(raw) as Partial<StoredInstance>) : null;
    if (
      parsed &&
      typeof parsed.instanceId === 'string' &&
      typeof parsed.templateId === 'string' &&
      parsed.orgId === who.orgId &&
      parsed.userId === who.userId
    ) {
      const value: StoredInstance = {
        instanceId: parsed.instanceId,
        templateId: parsed.templateId,
        orgId: who.orgId,
        userId: who.userId,
      };
      held.set(key, value);
      return { instanceId: value.instanceId, templateId: value.templateId };
    }
  } catch {
    logWarning('assessments.update_instance_storage_read_unavailable');
  }
  return null;
}

/** Keep (or, with `null`, forget) this person's unlinked instance for this original. */
export function rememberUnlinkedUpdateInstance(
  owner: UpdateInstanceOwner | null | undefined,
  originalId: string,
  value: UnlinkedUpdateInstance | null,
): void {
  const who = usableOwner(owner);
  if (!who || !originalId) return;
  const key = keyOf(who, originalId);
  const stored: StoredInstance | null = value
    ? { ...value, orgId: who.orgId, userId: who.userId }
    : null;
  if (stored) held.set(key, stored);
  else held.delete(key);
  try {
    const storageKey = UNLINKED_UPDATE_INSTANCE_STORAGE_PREFIX + key;
    if (stored) window.sessionStorage.setItem(storageKey, JSON.stringify(stored));
    else window.sessionStorage.removeItem(storageKey);
  } catch {
    logWarning('assessments.update_instance_storage_write_unavailable');
  }
}

/**
 * Forget every kept instance, of every person: the module map and every
 * `sessionStorage` entry under the prefix (including entries written before
 * they were keyed by person). Called by `AuthContext` on an explicit sign-out
 * and on any change of person or organisation (never on a session expiry).
 */
export function forgetUnlinkedUpdateInstances(): void {
  held.clear();
  try {
    const storage = window.sessionStorage;
    const doomed: string[] = [];
    for (let i = 0; i < storage.length; i++) {
      const k = storage.key(i);
      if (k && k.startsWith(UNLINKED_UPDATE_INSTANCE_STORAGE_PREFIX)) doomed.push(k);
    }
    for (const k of doomed) storage.removeItem(k);
  } catch {
    logWarning('assessments.update_instance_storage_write_unavailable');
  }
}
